/**
 * Alpine.js date-range picker for the analytics dashboard.
 *
 * Replaces the plain range <select> + custom date inputs with a single popover:
 * a trigger showing the applied range, an inline month calendar with range
 * highlighting, From/To read-outs, a "Suggestions" preset column, and Apply/Cancel.
 *
 * It submits the same GET params the dashboard form already understands:
 *   - a preset:  range=<key>            (start/end cleared)
 *   - a custom:  range=custom&start=YYYY-MM-DD&end=YYYY-MM-DD
 *
 * Registered via Alpine.data('rangePicker', rangePicker) in ./alpinejs.js.
 */

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function ymd(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseISO(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function prettyISO(s) {
  const d = parseISO(s);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function rangePicker(seed = {}) {
  return {
    open: false,
    presets: seed.presets || [],
    appliedLabel: seed.appliedLabel || "Select range",
    rangeValue: seed.rangeValue || "6m",
    startISO: seed.startISO || "",
    endISO: seed.endISO || "",
    hoverISO: "",
    viewYear: 0,
    viewMonth: 0,
    weekdays: WEEKDAYS,

    init() {
      // Open the calendar on the selected start month, else the current month.
      const anchor = parseISO(this.startISO) || new Date();
      this.viewYear = anchor.getFullYear();
      this.viewMonth = anchor.getMonth();
    },

    toggle() {
      this.open = !this.open;
      if (this.open) {
        const anchor = parseISO(this.startISO) || new Date();
        this.viewYear = anchor.getFullYear();
        this.viewMonth = anchor.getMonth();
      }
    },

    get title() {
      return `${MONTHS[this.viewMonth]} ${this.viewYear}`;
    },

    // Month grid as an array of Date|null (leading blanks for a Monday-start week).
    get grid() {
      const first = new Date(this.viewYear, this.viewMonth, 1);
      const lead = (first.getDay() + 6) % 7; // Mon=0 … Sun=6
      const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
      const cells = [];
      for (let i = 0; i < lead; i++) cells.push(null);
      for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(this.viewYear, this.viewMonth, d));
      return cells;
    },

    get fromLabel() {
      return this.startISO ? prettyISO(this.startISO) : "Start";
    },
    get toLabel() {
      return this.endISO ? prettyISO(this.endISO) : "End";
    },
    get canApply() {
      return !!this.startISO && !!this.endISO;
    },

    prev() {
      if (this.viewMonth === 0) { this.viewMonth = 11; this.viewYear--; }
      else this.viewMonth--;
    },
    next() {
      if (this.viewMonth === 11) { this.viewMonth = 0; this.viewYear++; }
      else this.viewMonth++;
    },

    pick(cell) {
      if (!cell) return;
      const iso = ymd(cell);
      // Start a new range when nothing is pending or a full range already exists.
      if (!this.startISO || (this.startISO && this.endISO)) {
        this.startISO = iso;
        this.endISO = "";
        this.hoverISO = "";
        return;
      }
      // Second click completes the range (swap if before start).
      if (iso < this.startISO) {
        this.endISO = this.startISO;
        this.startISO = iso;
      } else {
        this.endISO = iso;
      }
    },

    hover(cell) {
      if (cell && this.startISO && !this.endISO) this.hoverISO = ymd(cell);
    },

    // Styling helper for a day cell.
    dayClass(cell) {
      if (!cell) return "h-8 invisible";
      const iso = ymd(cell);
      const base = "h-8 cursor-pointer select-none rounded-md text-sm leading-8 text-center transition-colors";
      const isStart = iso === this.startISO;
      const isEnd = iso === this.endISO;
      // effective end for hover preview
      const previewEnd = this.endISO || (this.hoverISO && this.hoverISO >= this.startISO ? this.hoverISO : "");
      const inRange = this.startISO && previewEnd && iso > this.startISO && iso < previewEnd;
      if (isStart || isEnd) {
        return `${base} bg-blue-600 text-white font-semibold`;
      }
      if (inRange) {
        return `${base} bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200`;
      }
      return `${base} text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700`;
    },

    dayLabel(cell) {
      return cell ? cell.getDate() : "";
    },

    _form() {
      return this.$el.closest("form");
    },
    _setHidden(name, value) {
      const form = this._form();
      if (!form) return;
      const input = form.querySelector(`input[name="${name}"]`);
      if (input) input.value = value;
    },

    choosePreset(key) {
      this._setHidden("range", key);
      this._setHidden("start", "");
      this._setHidden("end", "");
      this.open = false;
      this._form().submit();
    },

    apply() {
      if (!this.canApply) return;
      this._setHidden("range", "custom");
      this._setHidden("start", this.startISO);
      this._setHidden("end", this.endISO);
      this.open = false;
      this._form().submit();
    },

    clear() {
      this.startISO = "";
      this.endISO = "";
      this.hoverISO = "";
    },
  };
}
